import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  ArrowDown,
  Inbox,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { usePendingChanges, useResolveChanges } from '../hooks/useStockSync';

const RULE_LABELS: Record<string, { label: string; variant: 'destructive' | 'secondary' | 'outline' }> = {
  zero_out: { label: 'Zero Out', variant: 'destructive' },
  drop_units: { label: 'Large Drop', variant: 'secondary' },
  drop_percent: { label: 'Drop %', variant: 'outline' },
  // BLU-553: hard downward-push blocker (drop vs last-pushed baseline exceeds
  // the org delta-block threshold). Same qty columns as the drop rules —
  // last_pushed_qty vs current_erp_qty + delta.
  delta_block: { label: 'Blocked drop', variant: 'secondary' },
};

export function ApprovalQueue() {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data, isLoading } = usePendingChanges(page);
  const resolve = useResolveChanges();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((item) => item.id)));
    }
  };

  const handleResolve = (action: 'approve' | 'dismiss', ids?: string[]) => {
    const resolveIds = ids ?? Array.from(selected);
    if (resolveIds.length === 0) return;
    resolve.mutate({ ids: resolveIds, action }, {
      onSuccess: () => setSelected(new Set()),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-4">
          <Inbox className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-lg font-medium">No pending approvals</p>
        <p className="text-sm text-muted-foreground mt-1">
          Stock changes within thresholds are pushed automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk action bar */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          {selected.size > 0 ? `${selected.size} selected` : `${total} pending`}
        </span>
        <div className="flex-1" />
        {selected.size > 0 && (
          <>
            <Button
              size="sm"
              onClick={() => handleResolve('approve')}
              disabled={resolve.isPending}
            >
              {resolve.isPending
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
              Approve ({selected.size})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleResolve('dismiss')}
              disabled={resolve.isPending}
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
              Dismiss ({selected.size})
            </Button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={selected.size === items.length && items.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">PrintRun Qty</TableHead>
              <TableHead className="text-right">Last Pushed</TableHead>
              <TableHead className="text-right">Delta</TableHead>
              <TableHead>Rule</TableHead>
              <TableHead>Time</TableHead>
              <TableHead className="w-[140px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const ruleInfo = RULE_LABELS[item.triggered_rule] ?? { label: item.triggered_rule, variant: 'outline' as const };
              const isZeroOut = item.triggered_rule === 'zero_out';

              return (
                <TableRow
                  key={item.id}
                  className={cn(isZeroOut && 'bg-red-50/50 dark:bg-red-950/10')}
                >
                  <TableCell>
                    <Checkbox
                      checked={selected.has(item.id)}
                      onCheckedChange={() => toggleSelect(item.id)}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">
                    {item.product_name ?? '-'}
                  </TableCell>
                  <TableCell className="text-sm">{item.location_name ?? '-'}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {item.current_erp_qty}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {item.last_pushed_qty}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={cn(
                      'inline-flex items-center gap-0.5 tabular-nums font-medium',
                      item.delta < 0 ? 'text-red-600' : 'text-green-600',
                    )}>
                      <ArrowDown className={cn('h-3 w-3', item.delta >= 0 && 'rotate-180')} />
                      {Math.abs(item.delta)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={ruleInfo.variant} className="text-xs">
                      {isZeroOut && <AlertTriangle className="h-3 w-3 mr-1" />}
                      {ruleInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
                        onClick={() => handleResolve('approve', [item.id])}
                        disabled={resolve.isPending}
                        aria-label="Approve"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                        onClick={() => handleResolve('dismiss', [item.id])}
                        disabled={resolve.isPending}
                        aria-label="Dismiss"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
